import type { z } from 'zod';

export type SearchShape = Readonly<Record<string, z.ZodCatch<z.ZodTypeAny>>>;

export type SearchValues<S extends SearchShape> = z.output<z.ZodObject<S>>;

export type SearchValue = string | number | boolean | null | undefined;

export type SearchDefaults = Readonly<Record<string, SearchValue>>;

const isBlank = (value: SearchValue): boolean => value === undefined || value === null || value === '';

export const parseSearch = <S extends SearchShape>(schema: z.ZodObject<S>, params: URLSearchParams): SearchValues<S> =>
  schema.parse(
    Object.keys(schema.shape).reduce<Record<string, string | undefined>>((values, key) => ({ ...values, [key]: params.get(key) ?? undefined }), {}),
  );

const writeValues = (params: URLSearchParams, values: SearchDefaults, defaults: SearchDefaults): URLSearchParams => {
  const next = new URLSearchParams(params);
  Object.entries(values).forEach(([key, value]) => {
    if (isBlank(value) || value === defaults[key]) {
      next.delete(key);
      return;
    }
    next.set(key, String(value));
  });
  return next;
};

export const writeSearch = <V extends SearchDefaults>(params: URLSearchParams, values: Partial<V>, defaults: V): URLSearchParams =>
  writeValues(params, values, defaults);
